import React, { useEffect, useState, useRef } from 'react';
import { UserIcon, ShieldIcon, BellIcon, CameraIcon, SaveIcon, EyeIcon, EyeOffIcon, CheckIcon, MapPinIcon, DollarSignIcon, ShoppingCartIcon, FileTextIcon, UsersIcon, CalendarIcon, LockIcon, KeyIcon, AlertTriangleIcon, GlobeIcon, Loader2Icon, SettingsIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../context/AuthContext';
import { getGoogleApiKey, setGoogleApiKey, validateGoogleApiKey, hasGoogleApiKey } from '../lib/googleMaps';
type Tab = 'perfil' | 'seguridad' | 'notificaciones' | 'integraciones';
const AVATAR_KEY = 'ventastrack_avatar';
const NOTIFS_KEY = 'ventastrack_notifs';
const defaultNotifs = {
  visitasCompletadas: true,
  nuevosCobros: true,
  nuevasVentas: false,
  reportesDiarios: true,
  alertasEquipo: false,
  recordatoriosVisita: true
};
export type NotifPrefs = typeof defaultNotifs;
export function loadNotifPrefs(): NotifPrefs {
  try {
    const saved = localStorage.getItem(NOTIFS_KEY);
    if (saved) return {
      ...defaultNotifs,
      ...JSON.parse(saved)
    };
  } catch {}
  return defaultNotifs;
}
export function loadAvatarUrl(): string {
  return localStorage.getItem(AVATAR_KEY) || '';
}
export function SettingsPage() {
  const {
    user
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  // Permission helpers for settings sub-tabs
  const canSeePerfil = isAdmin || user?.permisos?.settings_perfil === true || user?.permisos?.settings_perfil === undefined;
  const canSeeSeguridad = isAdmin || user?.permisos?.settings_seguridad === true || user?.permisos?.settings_seguridad === undefined;
  const canSeeNotificaciones = isAdmin || user?.permisos?.settings_notificaciones === true || user?.permisos?.settings_notificaciones === undefined;
  const canSeeIntegraciones = isAdmin || user?.permisos?.settings_integraciones === true;
  const [activeTab, setActiveTab] = useState<Tab>('perfil');
  // Perfil
  const [avatarUrl, setAvatarUrl] = useState<string>(loadAvatarUrl);
  const [nombre, setNombre] = useState(user?.nombre || '');
  const [email, setEmail] = useState(user?.email || '');
  const [perfilSaved, setPerfilSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Seguridad
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  // Notificaciones
  const [notifs, setNotifs] = useState<NotifPrefs>(loadNotifPrefs);
  const [notifSaved, setNotifSaved] = useState(false);
  // Integraciones
  const [googleKey, setGoogleKey] = useState(getGoogleApiKey);
  const [googleKeyInput, setGoogleKeyInput] = useState(getGoogleApiKey);
  const [isValidatingKey, setIsValidatingKey] = useState(false);
  const [keyValidation, setKeyValidation] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [integrationSaved, setIntegrationSaved] = useState(false);
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 10) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd) || /[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };
  const strengthColors = ['bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500'];
  const strengthLabels = ['Muy débil', 'Débil', 'Buena', 'Fuerte'];
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const base64 = ev.target?.result as string;
      setAvatarUrl(base64);
      localStorage.setItem(AVATAR_KEY, base64);
      window.dispatchEvent(new Event('avatar-updated'));
    };
    reader.readAsDataURL(file);
  };
  const handleSavePerfil = () => {
    setPerfilSaved(true);
    setTimeout(() => setPerfilSaved(false), 2500);
  };
  const handleSavePassword = () => {
    setPasswordError('');
    if (!currentPassword) {
      setPasswordError('Ingresa tu contraseña actual');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    setPasswordSaved(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => setPasswordSaved(false), 2500);
  };
  const handleToggleNotif = (key: keyof NotifPrefs) => {
    setNotifs(prev => {
      const updated = {
        ...prev,
        [key]: !prev[key]
      };
      localStorage.setItem(NOTIFS_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event('notifs-updated'));
      return updated;
    });
  };
  const handleSaveNotifs = () => {
    localStorage.setItem(NOTIFS_KEY, JSON.stringify(notifs));
    window.dispatchEvent(new Event('notifs-updated'));
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2500);
  };
  const handleValidateGoogleKey = async () => {
    if (!googleKeyInput.trim()) return;
    setIsValidatingKey(true);
    setKeyValidation('idle');
    try {
      const isValid = await validateGoogleApiKey(googleKeyInput.trim());
      setKeyValidation(isValid ? 'valid' : 'invalid');
    } catch {
      setKeyValidation('invalid');
    } finally {
      setIsValidatingKey(false);
    }
  };
  const handleSaveGoogleKey = () => {
    setGoogleApiKey(googleKeyInput.trim());
    setGoogleKey(googleKeyInput.trim());
    setIntegrationSaved(true);
    setTimeout(() => setIntegrationSaved(false), 2500);
  };
  const handleRemoveGoogleKey = () => {
    setGoogleApiKey('');
    setGoogleKey('');
    setGoogleKeyInput('');
    setKeyValidation('idle');
    setIntegrationSaved(true);
    setTimeout(() => setIntegrationSaved(false), 2500);
  };
  const notifItems: {
    key: keyof NotifPrefs;
    label: string;
    desc: string;
    icon: React.ReactNode;
    color: string;
  }[] = [{
    key: 'visitasCompletadas',
    label: 'Visitas completadas',
    desc: 'Cuando un vendedor completa una visita',
    icon: <MapPinIcon className="w-4 h-4" data-id="element-3122" />,
    color: 'bg-[#eef2fc] text-[#2952C4]'
  }, {
    key: 'nuevosCobros',
    label: 'Nuevos cobros',
    desc: 'Alertas de cobros registrados en el sistema',
    icon: <DollarSignIcon className="w-4 h-4" data-id="element-3123" />,
    color: 'bg-emerald-100 text-emerald-600'
  }, {
    key: 'nuevasVentas',
    label: 'Nuevas ventas',
    desc: 'Alertas de ventas registradas',
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-3124" />,
    color: 'bg-blue-100 text-blue-600'
  }, {
    key: 'reportesDiarios',
    label: 'Reportes diarios',
    desc: 'Resumen diario de actividad del equipo',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-3125" />,
    color: 'bg-[#d5e0f7] text-[#1E3D96]'
  }, {
    key: 'alertasEquipo',
    label: 'Alertas del equipo',
    desc: 'Actividad y cambios del equipo comercial',
    icon: <UsersIcon className="w-4 h-4" data-id="element-3126" />,
    color: 'bg-amber-100 text-amber-600'
  }, {
    key: 'recordatoriosVisita',
    label: 'Recordatorios de visita',
    desc: 'Recordatorios de próximas visitas programadas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-3127" />,
    color: 'bg-rose-100 text-rose-600'
  }];
  const allTabs: {
    id: Tab;
    label: string;
    icon: React.ReactNode;
    visible: boolean;
  }[] = [{
    id: 'perfil',
    label: 'Perfil',
    icon: <UserIcon className="w-5 h-5" data-id="element-3128" />,
    visible: canSeePerfil
  }, {
    id: 'seguridad',
    label: 'Seguridad',
    icon: <ShieldIcon className="w-5 h-5" data-id="element-3129" />,
    visible: canSeeSeguridad
  }, {
    id: 'notificaciones',
    label: 'Notificaciones',
    icon: <BellIcon className="w-5 h-5" data-id="element-3130" />,
    visible: canSeeNotificaciones
  }, {
    id: 'integraciones',
    label: 'Integraciones',
    icon: <GlobeIcon className="w-5 h-5" data-id="element-3131" />,
    visible: canSeeIntegraciones
  }];
  const tabs = allTabs.filter(t => t.visible);
  // Auto-select first visible tab if current is hidden
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);
  const activeNotifCount = Object.values(notifs).filter(Boolean).length;
  const pwdStrength = getPasswordStrength(newPassword);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-3132">
      <PageHeader title="Configuración" subtitle="Ajustes de cuenta y preferencias del sistema" icon={<SettingsIcon className="w-6 h-6" data-id="element-3134" />} data-id="element-3133" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-stagger-1" data-id="element-3135">
        {/* Sidebar */}
        <div className="md:col-span-1 space-y-4" data-id="element-3136">
          {/* User card */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center" data-id="element-3137">
            <div className="relative inline-block mb-4" data-id="element-3138">
              <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center shadow-lg mx-auto" style={{
              background: 'linear-gradient(135deg, #3B65D7, #2952C4)'
            }} data-id="element-3139">
                {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-3140" /> : <span className="text-2xl font-bold text-white" data-id="element-3141">
                    {getInitials(nombre || user?.nombre || '??')}
                  </span>}
              </div>
              <button onClick={() => fileInputRef.current?.click()} className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-xl flex items-center justify-center shadow-lg transition-colors" style={{
              backgroundColor: '#2952C4'
            }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1E3D96'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2952C4'} data-id="element-3142">
                <CameraIcon className="w-3.5 h-3.5 text-white" data-id="element-3143" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} data-id="element-3144" />
            </div>
            <p className="font-bold text-slate-900 font-heading" data-id="element-3145">
              {user?.nombre}
            </p>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-3146">{user?.rolNombre}</p>
            <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-200" data-id="element-3147">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-3148" />
              Activo
            </span>
          </div>

          {/* Tab navigation */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3149">
            {tabs.map((tab, idx) => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-5 py-4 text-sm font-medium transition-all ${idx < tabs.length - 1 ? 'border-b border-slate-100' : ''}`} style={activeTab === tab.id ? {
            backgroundColor: '#eef2fc',
            color: '#1E3D96',
            borderRight: '2px solid #2952C4'
          } : {}} onMouseEnter={e => {
            if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#f8fafc';
          }} onMouseLeave={e => {
            if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '';
          }} data-id="element-3150">
                <span style={activeTab === tab.id ? {
              color: '#2952C4'
            } : {
              color: '#94a3b8'
            }} data-id="element-3151">
                  {tab.icon}
                </span>
                <span className={activeTab === tab.id ? 'font-heading' : 'text-slate-600 font-heading'} data-id="element-3152">
                  {tab.label}
                </span>
                {tab.id === 'notificaciones' && <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{
              backgroundColor: '#eef2fc',
              color: '#2952C4'
            }} data-id="element-3153">
                    {activeNotifCount}
                  </span>}
              </button>)}
          </div>
        </div>

        {/* Right content */}
        <div className="md:col-span-3" data-id="element-3154">
          {/* PERFIL TAB */}
          {activeTab === 'perfil' && canSeePerfil && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3155">
              <div className="px-8 py-6 border-b border-slate-100" style={{
            background: 'linear-gradient(90deg, #eef2fc, #f8fafc)'
          }} data-id="element-3156">
                <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-3157">
                  Información del Perfil
                </h3>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-3158">
                  Actualiza tu información personal y foto de perfil
                </p>
              </div>

              <div className="p-8 space-y-6" data-id="element-3159">
                <div className="flex items-center gap-6 p-5 bg-slate-50 rounded-2xl border border-dashed border-slate-200" data-id="element-3160">
                  <div className="relative flex-shrink-0" data-id="element-3161">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center shadow-md" style={{
                  background: 'linear-gradient(135deg, #3B65D7, #2952C4)'
                }} data-id="element-3162">
                      {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-3163" /> : <span className="text-2xl font-bold text-white" data-id="element-3164">
                          {getInitials(nombre || user?.nombre || '??')}
                        </span>}
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-3165">
                    <p className="text-sm font-semibold text-slate-900 mb-1" data-id="element-3166">
                      Foto de perfil
                    </p>
                    <p className="text-xs text-slate-500 mb-3" data-id="element-3167">
                      JPG, PNG o GIF. Máximo 5MB. Se guarda automáticamente.
                    </p>
                    <div className="flex items-center gap-2" data-id="element-3168">
                      <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-xl transition-colors shadow-sm" style={{
                    backgroundColor: '#2952C4'
                  }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1E3D96'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2952C4'} data-id="element-3169">
                        <CameraIcon className="w-4 h-4" data-id="element-3170" />
                        Subir foto
                      </button>
                      {avatarUrl && <button onClick={() => {
                    setAvatarUrl('');
                    localStorage.removeItem(AVATAR_KEY);
                    window.dispatchEvent(new Event('avatar-updated'));
                  }} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors" data-id="element-3171">
                          Eliminar
                        </button>}
                    </div>
                  </div>
                </div>

                {isAdmin ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-3172">
                    <Input label="Nombre Completo" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Tu nombre completo" data-id="element-3173" />
                    <Input label="Correo Electrónico" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@correo.com" data-id="element-3174" />
                  </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-3175">
                    <div data-id="element-3176">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3177">
                        Nombre Completo
                      </label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-3178">
                        <UserIcon className="w-4 h-4 text-slate-400" data-id="element-3179" />
                        <span className="text-sm text-slate-600" data-id="element-3180">
                          {user?.nombre}
                        </span>
                        <span className="ml-auto text-xs text-slate-400" data-id="element-3181">
                          Solo admin
                        </span>
                      </div>
                    </div>
                    <div data-id="element-3182">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3183">
                        Correo Electrónico
                      </label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-3184">
                        <span className="text-sm text-slate-600" data-id="element-3185">
                          {user?.email || '—'}
                        </span>
                        <span className="ml-auto text-xs text-slate-400" data-id="element-3186">
                          Solo admin
                        </span>
                      </div>
                    </div>
                  </div>}

                <div data-id="element-3187">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3188">
                    Rol del Sistema
                  </label>
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-3189">
                    <ShieldIcon className="w-4 h-4 text-slate-400" data-id="element-3190" />
                    <span className="text-sm text-slate-600" data-id="element-3191">
                      {user?.rolNombre}
                    </span>
                    <span className="ml-auto text-xs text-slate-400" data-id="element-3192">
                      No editable
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3" data-id="element-3193">
                {perfilSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3194">
                    <CheckIcon className="w-4 h-4" data-id="element-3195" />
                    Cambios guardados
                  </div>}
                <Button onClick={handleSavePerfil} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-3197" />} data-id="element-3196">
                  Guardar Cambios
                </Button>
              </div>
            </div>}

          {/* SEGURIDAD TAB */}
          {activeTab === 'seguridad' && canSeeSeguridad && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3198">
              <div className="px-8 py-6 border-b border-slate-100 bg-slate-50" data-id="element-3199">
                <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-3200">
                  Seguridad de la Cuenta
                </h3>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-3201">
                  Actualiza tu contraseña para mantener tu cuenta segura
                </p>
              </div>

              <div className="p-8 space-y-6" data-id="element-3202">
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-3203">
                  <AlertTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3204" />
                  <div data-id="element-3205">
                    <p className="text-sm font-semibold text-amber-800" data-id="element-3206">
                      Recomendación de seguridad
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5" data-id="element-3207">
                      Usa una contraseña de al menos 10 caracteres con letras,
                      números y símbolos.
                    </p>
                  </div>
                </div>

                <div className="space-y-5" data-id="element-3208">
                  <div data-id="element-3209">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3210">
                      Contraseña Actual
                    </label>
                    <div className="relative" data-id="element-3211">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-3212">
                        <KeyIcon className="w-4 h-4" data-id="element-3213" />
                      </div>
                      <input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-12 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white" style={{
                    '--tw-ring-color': 'rgba(41,82,196,0.2)',
                    '--tw-border-opacity': '1'
                  } as React.CSSProperties} data-id="element-3214" />
                      <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-3215">
                        {showCurrent ? <EyeOffIcon className="w-4 h-4" data-id="element-3216" /> : <EyeIcon className="w-4 h-4" data-id="element-3217" />}
                      </button>
                    </div>
                  </div>

                  <div data-id="element-3218">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3219">
                      Nueva Contraseña
                    </label>
                    <div className="relative" data-id="element-3220">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-3221">
                        <LockIcon className="w-4 h-4" data-id="element-3222" />
                      </div>
                      <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-12 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white" data-id="element-3223" />
                      <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-3224">
                        {showNew ? <EyeOffIcon className="w-4 h-4" data-id="element-3225" /> : <EyeIcon className="w-4 h-4" data-id="element-3226" />}
                      </button>
                    </div>
                    {newPassword && <div className="mt-3" data-id="element-3227">
                        <div className="flex gap-1.5 mb-1.5" data-id="element-3228">
                          {[0, 1, 2, 3].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i < pwdStrength ? strengthColors[pwdStrength - 1] : 'bg-slate-200'}`} data-id="element-3229" />)}
                        </div>
                        <p className={`text-xs font-medium ${pwdStrength <= 1 ? 'text-rose-500' : pwdStrength === 2 ? 'text-amber-500' : pwdStrength === 3 ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-3230">
                          {strengthLabels[pwdStrength - 1] || 'Muy débil'}
                        </p>
                      </div>}
                  </div>

                  <div data-id="element-3231">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3232">
                      Confirmar Nueva Contraseña
                    </label>
                    <div className="relative" data-id="element-3233">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-3234">
                        <LockIcon className="w-4 h-4" data-id="element-3235" />
                      </div>
                      <input type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" className={`w-full pl-10 pr-12 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white ${confirmPassword && newPassword ? confirmPassword === newPassword ? 'border-emerald-400' : 'border-rose-400' : 'border-slate-200'}`} data-id="element-3236" />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-3237">
                        {showConfirm ? <EyeOffIcon className="w-4 h-4" data-id="element-3238" /> : <EyeIcon className="w-4 h-4" data-id="element-3239" />}
                      </button>
                    </div>
                    {confirmPassword && newPassword && <p className={`mt-1.5 text-xs font-medium flex items-center gap-1 ${confirmPassword === newPassword ? 'text-emerald-600' : 'text-rose-500'}`} data-id="element-3240">
                        {confirmPassword === newPassword ? <>
                            <CheckIcon className="w-3 h-3" data-id="element-3241" />
                            Las contraseñas coinciden
                          </> : '✗ Las contraseñas no coinciden'}
                      </p>}
                  </div>
                </div>

                {passwordError && <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-3242">
                    <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" data-id="element-3243" />
                    {passwordError}
                  </div>}
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3" data-id="element-3244">
                {passwordSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3245">
                    <CheckIcon className="w-4 h-4" data-id="element-3246" />
                    Contraseña actualizada
                  </div>}
                <Button onClick={handleSavePassword} leftIcon={<ShieldIcon className="w-4 h-4" data-id="element-3248" />} data-id="element-3247">
                  Actualizar Contraseña
                </Button>
              </div>
            </div>}

          {/* NOTIFICACIONES TAB */}
          {activeTab === 'notificaciones' && canSeeNotificaciones && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3249">
              <div className="px-8 py-6 border-b border-slate-100" style={{
            background: 'linear-gradient(90deg, #eef2fc, #f8fafc)'
          }} data-id="element-3250">
                <div className="flex items-center justify-between" data-id="element-3251">
                  <div data-id="element-3252">
                    <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-3253">
                      Notificaciones
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-3254">
                      Controla qué alertas deseas recibir
                    </p>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-3255">
                    <BellIcon className="w-4 h-4" style={{
                  color: '#2952C4'
                }} data-id="element-3256" />
                    <span className="text-sm font-bold text-slate-900" data-id="element-3257">
                      {activeNotifCount}
                    </span>
                    <span className="text-xs text-slate-500" data-id="element-3258">activas</span>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-3" data-id="element-3259">
                {notifItems.map(item => <div key={item.key} className="flex items-center justify-between p-4 rounded-xl border transition-all" style={notifs[item.key] ? {
              borderColor: '#aac1ef',
              backgroundColor: 'rgba(238,242,252,0.5)'
            } : {
              borderColor: '#f1f5f9',
              backgroundColor: 'white'
            }} data-id="element-3260">
                    <div className="flex items-center gap-4" data-id="element-3261">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`} data-id="element-3262">
                        {item.icon}
                      </div>
                      <div data-id="element-3263">
                        <p className="text-sm font-semibold text-slate-900" data-id="element-3264">
                          {item.label}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-3265">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4 flex-shrink-0" data-id="element-3266">
                      <input type="checkbox" className="sr-only peer" checked={notifs[item.key]} onChange={() => handleToggleNotif(item.key)} data-id="element-3267" />
                      <div className="brand-toggle w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all shadow-inner" data-id="element-3268" />
                    </label>
                  </div>)}
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-3269">
                <p className="text-sm text-slate-500" data-id="element-3270">
                  <span className="font-semibold text-slate-700" data-id="element-3271">
                    {activeNotifCount}
                  </span>{' '}
                  de {notifItems.length} notificaciones activas
                </p>
                <div className="flex items-center gap-3" data-id="element-3272">
                  {notifSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3273">
                      <CheckIcon className="w-4 h-4" data-id="element-3274" />
                      Guardado
                    </div>}
                  <Button onClick={handleSaveNotifs} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-3276" />} data-id="element-3275">
                    Guardar Preferencias
                  </Button>
                </div>
              </div>
            </div>}

          {/* INTEGRACIONES TAB */}
          {activeTab === 'integraciones' && canSeeIntegraciones && <div className="space-y-6" data-id="element-3277">
              {/* Google Maps Card */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3278">
                <div className="px-8 py-6 border-b border-slate-100" style={{
              background: 'linear-gradient(90deg, #e8f5e9, #f1f8e9)'
            }} data-id="element-3279">
                  <div className="flex items-center gap-4" data-id="element-3280">
                    <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center" data-id="element-3281">
                      <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" data-id="element-3282">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-3283" />
                        <circle cx="12" cy="9" r="3" fill="white" data-id="element-3284" />
                      </svg>
                    </div>
                    <div data-id="element-3285">
                      <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 font-heading" data-id="element-3286">
                        Google Maps Platform
                        {googleKey && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full" data-id="element-3287">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-3288" />
                            Conectado
                          </span>}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5" data-id="element-3289">
                        Mapas precisos, autocomplete de direcciones y GPS en
                        República Dominicana
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8 space-y-6" data-id="element-3290">
                  {/* Benefits */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-3291">
                    {[{
                  icon: '🗺️',
                  label: 'Mapas actualizados RD',
                  desc: 'Los más precisos disponibles'
                }, {
                  icon: '🔍',
                  label: 'Autocomplete',
                  desc: 'Búsqueda moderna de direcciones'
                }, {
                  icon: '📍',
                  label: 'GPS preciso',
                  desc: 'Ubicación exacta de farmacias'
                }, {
                  icon: '🧭',
                  label: 'Detección de llegada',
                  desc: 'Verificar visitas automáticamente'
                }].map(b => <div key={b.label} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl" data-id="element-3292">
                        <span className="text-lg" data-id="element-3293">{b.icon}</span>
                        <div data-id="element-3294">
                          <p className="text-sm font-semibold text-slate-900" data-id="element-3295">
                            {b.label}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-3296">{b.desc}</p>
                        </div>
                      </div>)}
                  </div>

                  {/* API Key Input */}
                  <div data-id="element-3297">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3298">
                      API Key de Google Maps
                    </label>
                    <div className="flex gap-2" data-id="element-3299">
                      <div className="relative flex-1" data-id="element-3300">
                        <KeyIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3301" />
                        <input type="password" value={googleKeyInput} onChange={e => {
                      setGoogleKeyInput(e.target.value);
                      setKeyValidation('idle');
                    }} placeholder="AIzaSy..." className={`w-full pl-10 pr-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white font-mono ${keyValidation === 'valid' ? 'border-emerald-400 ring-emerald-200' : keyValidation === 'invalid' ? 'border-rose-400 ring-rose-200' : 'border-slate-200'}`} data-id="element-3302" />
                      </div>
                      <Button variant="secondary" onClick={handleValidateGoogleKey} disabled={!googleKeyInput.trim() || isValidatingKey} data-id="element-3303">
                        {isValidatingKey ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-3304" /> : 'Verificar'}
                      </Button>
                    </div>
                    {keyValidation === 'valid' && <p className="mt-2 text-sm text-emerald-600 font-medium flex items-center gap-1" data-id="element-3305">
                        <CheckIcon className="w-4 h-4" data-id="element-3306" /> API Key válida —
                        Google Maps está listo
                      </p>}
                    {keyValidation === 'invalid' && <p className="mt-2 text-sm text-rose-600 font-medium flex items-center gap-1" data-id="element-3307">
                        <AlertTriangleIcon className="w-4 h-4" data-id="element-3308" /> API Key
                        inválida — Verifica que esté correcta y tenga los APIs
                        habilitados
                      </p>}
                  </div>

                  {/* Instructions */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl" data-id="element-3309">
                    <p className="text-sm font-semibold text-blue-800 mb-2" data-id="element-3310">
                      ¿Cómo obtener tu API Key?
                    </p>
                    <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside" data-id="element-3311">
                      <li data-id="element-3312">
                        Ve a{' '}
                        <a href="https://console.cloud.google.com/apis" target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-blue-900" data-id="element-3313">
                          Google Cloud Console
                        </a>
                      </li>
                      <li data-id="element-3314">Crea un proyecto nuevo o selecciona uno existente</li>
                      <li data-id="element-3315">
                        Habilita estos APIs:{' '}
                        <strong data-id="element-3316">Maps JavaScript API</strong>,{' '}
                        <strong data-id="element-3317">Places API</strong>,{' '}
                        <strong data-id="element-3318">Geocoding API</strong>
                      </li>
                      <li data-id="element-3319">
                        Ve a "Credenciales" → "Crear credenciales" → "API Key"
                      </li>
                      <li data-id="element-3320">Copia la API Key y pégala aquí</li>
                    </ol>
                  </div>
                </div>

                <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-3321">
                  <div data-id="element-3322">
                    {googleKey && <button onClick={handleRemoveGoogleKey} className="text-sm text-rose-600 hover:text-rose-700 font-medium hover:underline" data-id="element-3323">
                        Desconectar Google Maps
                      </button>}
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3324">
                    {integrationSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3325">
                        <CheckIcon className="w-4 h-4" data-id="element-3326" />
                        Guardado
                      </div>}
                    <Button onClick={handleSaveGoogleKey} disabled={!googleKeyInput.trim()} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-3328" />} data-id="element-3327">
                      Guardar API Key
                    </Button>
                  </div>
                </div>
              </div>

              {/* Status info */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-id="element-3329">
                <h4 className="text-sm font-bold text-slate-900 mb-3 font-heading" data-id="element-3330">
                  Estado de Integraciones
                </h4>
                <div className="space-y-3" data-id="element-3331">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-3332">
                    <div className="flex items-center gap-3" data-id="element-3333">
                      <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center" data-id="element-3334">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" data-id="element-3335">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-3336" />
                          <circle cx="12" cy="9" r="2.5" fill="white" data-id="element-3337" />
                        </svg>
                      </div>
                      <div data-id="element-3338">
                        <p className="text-sm font-medium text-slate-900" data-id="element-3339">
                          Google Maps
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-3340">
                          Mapas, Places, Geocoding
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full ${googleKey ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`} data-id="element-3341">
                      <span className={`w-1.5 h-1.5 rounded-full ${googleKey ? 'bg-emerald-500' : 'bg-slate-400'}`} data-id="element-3342" />
                      {googleKey ? 'Activo' : 'No configurado'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-3343">
                    <div className="flex items-center gap-3" data-id="element-3344">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-3345">
                        <MapPinIcon className="w-4 h-4 text-blue-600" data-id="element-3346" />
                      </div>
                      <div data-id="element-3347">
                        <p className="text-sm font-medium text-slate-900" data-id="element-3348">
                          Photon + OSM
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-3349">
                          Búsqueda gratuita (fallback)
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-3350">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-3351" />
                      Siempre activo
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}